import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { formatNumber } from "../../../redux/utils";
import { toWordsconver } from "../../../redux/api";
import logo from "../../../assets/img/KTSG.png";

// Styles that match the HTML version exactly
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 12,
    fontSize: 10,
    fontFamily: 'Helvetica',
    pageBreakAfter: 'always',
  },
  // Header section
  headerContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    paddingBottom: 5,
  },
  logo: {
    width: 70,
    height: 65,
    marginBottom: 3,
  },
  headerTitle1: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 1,
    marginTop: 1,
  },
  headerTitle2: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 1,
    marginTop: 1,
  },
  headerTitle3: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 1,
    marginTop: 1,
  },
  // Main body container
  bodyContainer: {
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: 10,
    display: 'flex',
    flexDirection: 'row',
    lineHeight: 1.3,
    minHeight: 500,
  },
  // Left column (75%)
  leftColumn: {
    width: '75%',
    minHeight: 500,
    borderRight: '1px solid #000',
    paddingRight: 10,
    paddingLeft: 6,
  },
  // Right column (25%)
  rightColumn: {
    width: '25%',
    paddingLeft: 10,
    paddingTop: 6,
    minHeight: 500,
  },
  // Payment voucher title
  pvTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica',
    fontWeight: 500,
    width: '100%',
    textAlign: 'right',
    marginBottom: 3,
    marginTop: 3,
    paddingRight: 50,
  },
  // Form fields
  formField: {
    fontSize: 11,
    marginBottom: 2,
    marginTop: 2,
  },
  formFieldBold: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
    marginTop: 2,
  },
  // Table styles
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    marginTop: 6,
    marginBottom: 6,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    borderStyle: 'solid',
    borderWidth: 1,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 5,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
  },
  tableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  tableCellLarge: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 4,
    fontSize: 10,
    height: 110, // Increased height for longer narrations
  },
  // Certification sections
  certificationSection: {
    width: '100%',
    borderBottom: '1px solid #000',
    paddingLeft: 2,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 5,
    paddingBottom: 4,
  },
  signatureRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  signatureText: {
    width: '70%',
    fontSize: 10,
    marginBottom: 1,
    marginTop: 1,
  },
  nameText: {
    marginLeft: 53,
    textTransform: 'uppercase',
    fontSize: 10,
    marginBottom: 2,
    marginTop: 1,
  },
  // Right column sections
  rightColumnSection: {
    width: '100%',
    marginBottom: 15,
  },
  rightColumnTitle: {
    width: '100%',
    textAlign: 'center',
    fontSize: 11,
    marginBottom: 5,
    marginTop: 3,
  },
  codeContainer: {
    width: '100%',
    border: '1px solid #000',
    display: 'flex',
    flexDirection: 'row',
    height: 24,
  },
  codeDigit: {
    textAlign: 'center',
    fontSize: 11,
    marginBottom: 0,
    marginTop: 0,
    paddingTop: 4,
    height: 22,
  },
  // Receipt section
  receiptSection: {
    width: '100%',
    paddingLeft: 2,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 6,
  },
  receiptText: {
    width: '100%',
    fontSize: 10,
    marginBottom: 1,
    marginTop: 1,
  },
  witnessSection: {
    display: 'flex',
    flexDirection: 'row',
    width: '60%',
    marginLeft: 40,
  },
  witnessColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  witnessColumnRight: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 40,
  },
  // Treasury section (right column bottom)
  treasurySection: {
    width: '100%',
    marginTop: 20,
  },
  treasuryText: {
    fontSize: 11,
    marginBottom: 2,
    marginTop: 2,
  },
  // Signature image styles
  signatureImage: {
    width: 35,
    height: 20,
    marginLeft: 5,
  },
});

export const ExactPVPDF = ({
  data = [],
  taxesApplied = [],
  user = {},
  isCapitalProject = false,
  capitalProjectData = null,
  multiplePVsData = null
}) => {
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Handle multiple PVs with taxes (our new implementation)
  if (multiplePVsData) {
    const { main_pv, tax_pvs = [], taxes = [] } = multiplePVsData;

    return (
      <Document>
        {/* Main PV Page with Tax Breakdown */}
        {main_pv && (
          <MultiplePVsMainPage
            pv={main_pv}
            taxes={taxes}
            capitalizeFirstLetter={capitalizeFirstLetter}
          />
        )}

        {/* Individual Tax PV Pages */}
        {tax_pvs.map((taxPV, index) => (
          <MultiplePVsTaxPage
            key={index}
            pv={taxPV}
            tax={taxes[index]}
            capitalizeFirstLetter={capitalizeFirstLetter}
          />
        ))}
      </Document>
    );
  }

  // Handle capital project PDF generation (legacy)
  if (isCapitalProject && capitalProjectData) {
    const { mainPV, taxPVs = [], taxes = [] } = capitalProjectData;

    return (
      <Document>
        {/* Main PV Page */}
        {mainPV && (
          <CapitalProjectMainPVPage
            pv={mainPV}
            taxes={taxes}
            capitalizeFirstLetter={capitalizeFirstLetter}
          />
        )}

        {/* Tax PV Pages */}
        {taxPVs.map((taxPV, index) => (
          <CapitalProjectTaxPVPage
            key={index}
            pv={taxPV}
            tax={taxes[index]}
            capitalizeFirstLetter={capitalizeFirstLetter}
          />
        ))}
      </Document>
    );
  }

  // Standard PV generation (existing logic)
  let totalAmount = data.reduce((a, b) => a + parseFloat(b.amount || 0), 0);
  let totalTaxesAmount = taxesApplied.reduce((a, b) => a + parseFloat(b.amount || 0), 0);
  let netAmount = parseFloat(totalAmount) - parseFloat(totalTaxesAmount);

  return (
    <Document>
      {data.map((item, index) => {
        const pendingSignature = item.history ? item.history.find((a) => a.pv_status === "Pending") : null;
        const checkedSignature = item.history ? item.history.find((a) => a.pv_status === "Checked") : null;
        const approvedSignature = item.history ? item.history.find((a) => a.pv_status === "Approved") : null;
        const redivedSignature = item.history ? item.history.find((a) => a.pv_status === "Reviewed") : null;

        return (
          <Page key={index} size="A3" style={styles.page}>
            {/* Header Section */}
            <View style={styles.headerContainer}>
              <Image src={logo} style={styles.logo} />
              <Text style={styles.headerTitle1}>KATSINA STATE GOVERNMENT</Text>
              <Text style={styles.headerTitle2}>MINISTRY OF FINANCE</Text>
              <Text style={styles.headerTitle3}>MINISTERIAL/DEPARTMENTAL PAYMENT VOUCHER</Text>
            </View>

            {/* Main Body Container */}
            <View style={styles.bodyContainer}>
              {/* Left Column (75%) */}
              <View style={styles.leftColumn}>
                <Text style={styles.pvTitle}>PAYMENT VOUCHER</Text>
                <Text style={styles.formFieldBold}>KTT.44</Text>

                <Text style={styles.formField}>
                  Organisation: {item?.mda_name || "......................................................................................................"}
                </Text>
                <Text style={styles.formField}>
                  Sub Organisation Name: {item?.sub || "......................................................................................................"}
                </Text>
                <Text style={styles.formField}>
                  Sub Sub Organisation Name: {item?.sub_sub || "......................................................................................................"}
                </Text>
                <Text style={styles.formField}>
                  Sub Sub Sub Organisation Name: {item?.sub_sub_sub || "......................................................................................................"}
                </Text>
                <Text style={styles.formField}>
                  Payment to: {item?.payment_to || "................................................................................................................."}
                </Text>

                {/* Main Table */}
                <View style={styles.table}>
                  {/* Table Header */}
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableHeader, { width: '15%' }]}>Date</Text>
                    <Text style={[styles.tableHeader, { width: '50%' }]}>Detailed description of service</Text>
                    <Text style={[styles.tableHeader, { width: '10%' }]}>Rate</Text>
                    <Text style={[styles.tableHeader, { width: '25%' }]}>Amount(NGN)</Text>
                  </View>

                  {/* Sub header for amount columns */}
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableHeader, { width: '15%' }]}></Text>
                    <Text style={[styles.tableHeader, { width: '50%' }]}></Text>
                    <Text style={[styles.tableHeader, { width: '10%' }]}></Text>
                    <Text style={[styles.tableHeader, { width: '20%' }]}>NGN</Text>
                    <Text style={[styles.tableHeader, { width: '5%' }]}>K</Text>
                  </View>

                  {/* Table Body */}
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCellLarge, { width: '15%' }]}></Text>
                    <Text style={[styles.tableCellLarge, {
                      width: '50%',
                      textAlign: 'left',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      paddingTop: 8
                    }]}>
                      {capitalizeFirstLetter(item?.narration) || ""}
                    </Text>
                    <Text style={[styles.tableCellLarge, { width: '10%' }]}></Text>
                    <Text style={[styles.tableCellLarge, { width: '20%', textAlign: 'right' }]}>
                      {formatNumber(item?.amount || 0)?.split(".")[0]}
                    </Text>
                    <Text style={[styles.tableCellLarge, { width: '5%' }]}>
                      {item?.amount?.toString()?.split(".")[1] || "00"}
                    </Text>
                  </View>

                  {/* Table Footer */}
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: '65%', textAlign: 'justify' }]}>
                      TOTAL AMOUNT IN WORDS: {toWordsconver(netAmount.toString().split(".")[0])?.toUpperCase()} NAIRA
                      {netAmount.toString().split(".")[1] && netAmount.toString().split(".")[1] !== "00"
                        ? ` AND ${toWordsconver(netAmount.toString().split(".")[1])?.toUpperCase()} KOBO`
                        : null} ONLY
                    </Text>
                    <Text style={[styles.tableCell, { width: '30%', textAlign: 'right' }]}>
                      {formatNumber(netAmount || 0)?.split(".")[0]}
                    </Text>
                    <Text style={[styles.tableCell, { width: '5%' }]}>
                      {netAmount?.toString()?.split(".")[1] || "00"}
                    </Text>
                  </View>
                </View>

                {/* First Certification Section */}
                <View style={styles.certificationSection}>
                  <Text style={styles.formField}>
                    We certify that the details above are in accordance with the relevant contract, regulations or other authority under which the Services/Goods were provided/purchased
                  </Text>
                  <View style={styles.signatureRow}>
                    <Text style={styles.signatureText}>Officer who prepared voucher, Signature:</Text>
                    {pendingSignature?.signature && (
                      <Image src={pendingSignature.signature} style={styles.signatureImage} />
                    )}
                  </View>
                  <Text style={styles.nameText}>
                    Name (in block letters): {pendingSignature?.fullname || ""}
                  </Text>
                  <View style={styles.signatureRow}>
                    <Text style={styles.signatureText}>Officer who checked voucher, Signature:</Text>
                    {checkedSignature?.signature && (
                      <Image src={checkedSignature.signature} style={styles.signatureImage} />
                    )}
                  </View>
                  <Text style={styles.nameText}>
                    Name (in block letters): {checkedSignature?.fullname || ""}
                  </Text>
                </View>

                {/* Second Certification Section */}
                <View style={styles.certificationSection}>
                  <Text style={styles.formField}>
                    We certify that the Service/Goods have been duly performed/received, that financial authority GW/.............. is held to incur this expenditure and that the relevant D V.E account entries have been made. We hereby authorized payment.
                  </Text>
                </View>

                {/* Accounting Officer Section */}
                <View style={{ marginBottom: 6, marginTop: 5 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.formField}>Signature:</Text>
                    {approvedSignature?.signature && (
                      <Image src={approvedSignature.signature} style={styles.signatureImage} />
                    )}
                  </View>
                  <Text style={styles.formField}>(Accounting Officer)</Text>
                  <View style={{ flexDirection: 'row', width: '100%', marginTop: 2 }}>
                    <Text style={[styles.formField, { width: '50%', textTransform: 'uppercase' }]}>
                      Name (in block letters): {approvedSignature?.fullname || ""}
                    </Text>
                    <Text style={[styles.formField, { width: '50%' }]}>
                      Date: {item?.pv_date}
                    </Text>
                  </View>
                </View>

                {/* Second Accounting Officer Section */}
                <View style={{ marginBottom: 8, marginTop: 5 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.formField}>Signature:</Text>
                    {redivedSignature?.signature && (
                      <Image src={redivedSignature.signature} style={styles.signatureImage} />
                    )}
                  </View>
                  <Text style={styles.formField}>(Accounting Officer)</Text>
                  <View style={{ flexDirection: 'row', width: '100%', marginTop: 2 }}>
                    <Text style={[styles.formField, { width: '50%', textTransform: 'uppercase' }]}>
                      Name (in block letters): {redivedSignature?.fullname || ""}
                    </Text>
                    <Text style={[styles.formField, { width: '50%' }]}>
                      Date: {item?.pv_date}
                    </Text>
                  </View>
                </View>

                {/* Payment Details Section */}
                <View style={[styles.certificationSection, { borderTop: '1px solid #000', marginTop: 6 }]}>
                  <View style={{ flexDirection: 'row', width: '100%' }}>
                    <Text style={[styles.formField, { width: '50%' }]}>Cheque No:</Text>
                    <Text style={[styles.formField, { width: '50%' }]}>
                      Account No: {item?.contractor_acc_no}
                    </Text>
                  </View>
                </View>

                <View style={[styles.certificationSection, { borderTop: 0, marginTop: 2 }]}>
                  <View style={{ flexDirection: 'row', width: '100%' }}>
                    <Text style={[styles.formField, { width: '50%' }]}>
                      Account Name: {item?.contractor_acc_name}
                    </Text>
                    <Text style={[styles.formField, { width: '50%' }]}>
                      Name of Bank: {item?.contractor_bank_name}
                    </Text>
                  </View>
                </View>

                {/* Receipt Section */}
                <View style={styles.receiptSection}>
                  <Text style={styles.receiptText}>
                    Received the sum of .....................................Naira and......................................Kobo in
                  </Text>
                  <Text style={styles.receiptText}>
                    acquisition of the above Account this.............................Day of..........................20..........
                  </Text>
                  <Text style={styles.receiptText}>
                    Amount in word.....................................Name of Payee...................................................
                  </Text>
                  <Text style={styles.receiptText}>
                    Witness in official Name..........................................Rank.................................................
                  </Text>

                  <View style={styles.witnessSection}>
                    <View style={styles.witnessColumn}>
                      <Text style={styles.receiptText}>Witness</Text>
                      <Text style={styles.receiptText}>Official</Text>
                    </View>
                    <View style={styles.witnessColumnRight}>
                      <Text style={styles.receiptText}>Name.......................................</Text>
                      <Text style={styles.receiptText}>Rank........................................</Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* Right Column (25%) */}
              <View style={styles.rightColumn}>
                {/* Voucher Number */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Voucher Number</Text>
                  <View style={styles.codeContainer}>
                    {String(item?.pv_code || "").split("").map((digit, digitIndex) => (
                      <Text
                        key={digitIndex}
                        style={[
                          styles.codeDigit,
                          {
                            width: `${100 / Math.max(String(item?.pv_code || "").length, 1)}%`,
                            borderRight: digitIndex < String(item?.pv_code || "").length - 1 ? '1px solid #000' : 0,
                          }
                        ]}
                      >
                        {digit}
                      </Text>
                    ))}
                  </View>
                </View>

                {/* Date */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Year/Month/Day</Text>
                  <View style={styles.codeContainer}>
                    {String(item?.date || "").split("").map((digit, digitIndex) => (
                      <Text
                        key={digitIndex}
                        style={[
                          styles.codeDigit,
                          {
                            width: `${100 / Math.max(String(item?.date || "").length, 1)}%`,
                            borderRight: digitIndex < String(item?.date || "").length - 1 ? '1px solid #000' : 0,
                          }
                        ]}
                      >
                        {digit}
                      </Text>
                    ))}
                  </View>
                </View>

                {/* Administrative Code */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Administrative Code</Text>
                  <View style={styles.codeContainer}>
                    {String(item?.mda_code || "").split("").map((digit, digitIndex) => (
                      <Text
                        key={digitIndex}
                        style={[
                          styles.codeDigit,
                          {
                            width: `${100 / Math.max(String(item?.mda_code || "").length, 1)}%`,
                            borderRight: digitIndex < String(item?.mda_code || "").length - 1 ? '1px solid #000' : 0,
                          }
                        ]}
                      >
                        {digit}
                      </Text>
                    ))}
                  </View>
                </View>

                {/* Economic Code */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Economic Code</Text>
                  <View style={styles.codeContainer}>
                    {String(item?.economic_code || "").split("").map((digit, digitIndex) => (
                      <Text
                        key={digitIndex}
                        style={[
                          styles.codeDigit,
                          {
                            width: `${100 / Math.max(String(item?.economic_code || "").length, 1)}%`,
                            borderRight: digitIndex < String(item?.economic_code || "").length - 1 ? '1px solid #000' : 0,
                          }
                        ]}
                      >
                        {digit}
                      </Text>
                    ))}
                  </View>
                </View>

                {/* Program Code */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Program Code</Text>
                  <View style={styles.codeContainer}>
                    {String(item?.program_code || "").split("").map((digit, digitIndex) => (
                      <Text
                        key={digitIndex}
                        style={[
                          styles.codeDigit,
                          {
                            width: `${100 / Math.max(String(item?.program_code || "").length, 1)}%`,
                            borderRight: digitIndex < String(item?.program_code || "").length - 1 ? '1px solid #000' : 0,
                          }
                        ]}
                      >
                        {digit}
                      </Text>
                    ))}
                  </View>
                </View>

                {/* Functional Code */}
                <View style={styles.rightColumnSection}>
                  <Text style={styles.rightColumnTitle}>Functional Code</Text>
                  <View style={styles.codeContainer}>
                    {/* Empty for functional code as in original */}
                  </View>
                </View>

                {/* Treasury Section */}
                <View style={styles.treasurySection}>
                  <Text style={styles.treasuryText}>Date received in Treasury</Text>
                  <Text style={styles.treasuryText}>............................................</Text>
                  <Text style={styles.treasuryText}>Check and passed at</Text>
                  <Text style={styles.treasuryText}>............................................</Text>
                  <Text style={styles.treasuryText}>For</Text>
                  <Text style={styles.treasuryText}>Payable only at</Text>
                  <Text style={styles.treasuryText}>Checked by internal Auditor</Text>
                  <Text style={styles.treasuryText}>(Name and Signature)</Text>
                </View>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

// Multiple PVs Main Page Component (matches our HTML implementation)
const MultiplePVsMainPage = ({ pv, taxes, capitalizeFirstLetter }) => {
  const totalTaxAmount = taxes.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
  const grossAmount = parseFloat(pv.original_amount || pv.amount || 0);
  const netAmount = parseFloat(pv.net_amount || (grossAmount - totalTaxAmount));

  const pendingSignature = pv.history ? pv.history.find((a) => a.pv_status === "Pending") : null;
  const checkedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Checked") : null;
  const approvedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Approved") : null;
  const redivedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Reviewed") : null;

  return (
    <Page size="A3" style={styles.page}>
      {/* Header Section */}
      <View style={styles.headerContainer}>
        <Image src={logo} style={styles.logo} />
        <Text style={styles.headerTitle1}>KATSINA STATE GOVERNMENT</Text>
        <Text style={styles.headerTitle2}>MINISTRY OF FINANCE</Text>
        <Text style={styles.headerTitle3}>MINISTERIAL/DEPARTMENTAL PAYMENT VOUCHER</Text>
      </View>

      {/* Main Body Container */}
      <View style={styles.bodyContainer}>
        {/* Left Column (75%) */}
        <View style={styles.leftColumn}>
          <Text style={styles.pvTitle}>PAYMENT VOUCHER</Text>
          <Text style={styles.formFieldBold}>KTT.44</Text>

          <Text style={styles.formField}>
            Organisation: {pv?.mda_name || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Organisation Name: {pv?.sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Organisation Name: {pv?.sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Sub Organisation Name: {pv?.sub_sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Payment to: {pv?.payment_to || "................................................................................................................."}
          </Text>

          {/* Main Table with Tax Breakdown */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}>Date</Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}>Detailed description of service</Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}>Rate</Text>
              <Text style={[styles.tableHeader, { width: '25%' }]}>Amount(NGN)</Text>
            </View>

            {/* Sub header for amount columns */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '20%' }]}>NGN</Text>
              <Text style={[styles.tableHeader, { width: '5%' }]}>K</Text>
            </View>

            {/* Main Service Description Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '15%', fontSize: 9 }]}>
                {pv?.pv_date ? new Date(pv.pv_date).toLocaleDateString() : ""}
              </Text>
              <Text style={[styles.tableCell, {
                width: '50%',
                textAlign: 'left',
                fontSize: 9,
                paddingTop: 5
              }]}>
                {capitalizeFirstLetter(pv?.narration || pv?.description) || ""}
              </Text>
              <Text style={[styles.tableCell, { width: '10%' }]}></Text>
              <Text style={[styles.tableCell, { width: '20%', textAlign: 'right', fontSize: 9 }]}>
                {formatNumber(grossAmount)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%', fontSize: 9 }]}>
                {grossAmount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>

            {/* Tax Deduction Rows */}
            {taxes.map((tax, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: '15%' }]}>
                  {pv?.pv_date ? new Date(pv.pv_date).toLocaleDateString() : ""}
                </Text>
                <Text style={[styles.tableCell, {
                  width: '50%',
                  fontSize: 9,
                  fontStyle: 'italic',
                  paddingLeft: 15
                }]}>
                  {tax.description} (Deduction)
                </Text>
                <Text style={[styles.tableCell, { width: '10%', textAlign: 'center', fontSize: 9 }]}>
                  {tax.rate ? `${tax.rate}%` : ""}
                </Text>
                <Text style={[styles.tableCell, { width: '20%', textAlign: 'right', fontSize: 9, color: '#d32f2f' }]}>
                  ({formatNumber(tax.amount)?.split(".")[0]})
                </Text>
                <Text style={[styles.tableCell, { width: '5%', fontSize: 9, color: '#d32f2f' }]}>
                  {tax.amount?.toString()?.split(".")[1] || "00"}
                </Text>
              </View>
            ))}

            {/* Net Amount Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '15%' }]}></Text>
              <Text style={[styles.tableCell, {
                width: '50%',
                fontSize: 9,
                fontFamily: 'Helvetica-Bold'
              }]}>
                NET AMOUNT PAYABLE
              </Text>
              <Text style={[styles.tableCell, { width: '10%' }]}></Text>
              <Text style={[styles.tableCell, { width: '20%', textAlign: 'right', fontSize: 9, fontFamily: 'Helvetica-Bold' }]}>
                {formatNumber(netAmount)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%', fontSize: 9, fontFamily: 'Helvetica-Bold' }]}>
                {netAmount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>

            {/* Table Footer */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '65%', textAlign: 'justify', fontSize: 9 }]}>
                TOTAL AMOUNT IN WORDS: {toWordsconver(netAmount.toString().split(".")[0])?.toUpperCase()} NAIRA
                {netAmount.toString().split(".")[1] && netAmount.toString().split(".")[1] !== "00"
                  ? ` AND ${toWordsconver(netAmount.toString().split(".")[1])?.toUpperCase()} KOBO`
                  : null} ONLY
              </Text>
              <Text style={[styles.tableCell, { width: '30%', textAlign: 'right', fontSize: 9, fontFamily: 'Helvetica-Bold' }]}>
                {formatNumber(netAmount)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%', fontSize: 9, fontFamily: 'Helvetica-Bold' }]}>
                {netAmount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>
          </View>

          {/* Rest of the standard PV layout */}
          <StandardPVCertificationSections
            pendingSignature={pendingSignature}
            checkedSignature={checkedSignature}
            approvedSignature={approvedSignature}
            redivedSignature={redivedSignature}
            pv={pv}
          />
        </View>

        {/* Right Column (25%) */}
        <StandardPVRightColumn pv={pv} />
      </View>
    </Page>
  );
};

// Multiple PVs Tax Page Component
const MultiplePVsTaxPage = ({ pv, tax, capitalizeFirstLetter }) => {
  const pendingSignature = pv.history ? pv.history.find((a) => a.pv_status === "Pending") : null;
  const checkedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Checked") : null;
  const approvedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Approved") : null;
  const redivedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Reviewed") : null;

  return (
    <Page size="A3" style={styles.page}>
      {/* Header Section */}
      <View style={styles.headerContainer}>
        <Image src={logo} style={styles.logo} />
        <Text style={styles.headerTitle1}>KATSINA STATE GOVERNMENT</Text>
        <Text style={styles.headerTitle2}>MINISTRY OF FINANCE</Text>
        <Text style={styles.headerTitle3}>PAYMENT VOUCHER</Text>
      </View>

      {/* Main Body Container */}
      <View style={styles.bodyContainer}>
        {/* Left Column (75%) */}
        <View style={styles.leftColumn}>
          {/* <Text style={styles.pvTitle}>TAX PAYMENT VOUCHER</Text> */}
          <Text style={styles.formFieldBold}>KTT.44 (Tax: {tax?.description})</Text>

          <Text style={styles.formField}>
            Organisation: {pv?.mda_name || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Organisation Name: {pv?.sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Organisation Name: {pv?.sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Sub Organisation Name: {pv?.sub_sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Payment to: {tax?.description} (Tax Authority)
          </Text>

          {/* Tax Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}>Date</Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}>Detailed description of service</Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}>Rate</Text>
              <Text style={[styles.tableHeader, { width: '25%' }]}>Amount(NGN)</Text>
            </View>

            {/* Sub header for amount columns */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '20%' }]}>NGN</Text>
              <Text style={[styles.tableHeader, { width: '5%' }]}>K</Text>
            </View>

            {/* Tax Description Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCellLarge, { width: '15%' }]}>
                {pv?.pv_date ? new Date(pv.pv_date).toLocaleDateString() : ""}
              </Text>
              <Text style={[styles.tableCellLarge, {
                width: '50%',
                textAlign: 'left',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                paddingTop: 8,
                fontSize: 9
              }]}>
                {tax?.description} - Tax deduction from: {capitalizeFirstLetter(pv?.narration || pv?.description) || ""}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '10%', textAlign: 'center', fontSize: 9 }]}>
                {tax?.rate ? `${tax.rate}%` : ''}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '20%', textAlign: 'right', fontSize: 9 }]}>
                {formatNumber(tax?.amount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '5%', fontSize: 9 }]}>
                {tax?.amount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>

            {/* Table Footer */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '65%', textAlign: 'justify', fontSize: 9 }]}>
                TOTAL AMOUNT IN WORDS: {toWordsconver((tax?.amount || 0).toString().split(".")[0])?.toUpperCase()} NAIRA
                {(tax?.amount || 0).toString().split(".")[1] && (tax?.amount || 0).toString().split(".")[1] !== "00"
                  ? ` AND ${toWordsconver((tax?.amount || 0).toString().split(".")[1])?.toUpperCase()} KOBO`
                  : null} ONLY
              </Text>
              <Text style={[styles.tableCell, { width: '30%', textAlign: 'right', fontSize: 9 }]}>
                {formatNumber(tax?.amount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%', fontSize: 9 }]}>
                {(tax?.amount || 0)?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>
          </View>

          {/* Rest of the standard PV layout */}
          <StandardPVCertificationSections
            pendingSignature={pendingSignature}
            checkedSignature={checkedSignature}
            approvedSignature={approvedSignature}
            redivedSignature={redivedSignature}
            pv={pv}
          />
        </View>

        {/* Right Column (25%) */}
        <StandardPVRightColumn pv={pv} />
      </View>
    </Page>
  );
};

// Capital Project Main PV Page Component
const CapitalProjectMainPVPage = ({ pv, taxes, capitalizeFirstLetter }) => {
  const totalTaxAmount = taxes.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
  const netAmount = parseFloat(pv.gross_amount || pv.amount || 0) - totalTaxAmount;

  const pendingSignature = pv.history ? pv.history.find((a) => a.pv_status === "Pending") : null;
  const checkedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Checked") : null;
  const approvedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Approved") : null;
  const redivedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Reviewed") : null;

  return (
    <Page size="A3" style={styles.page}>
      {/* Header Section */}
      <View style={styles.headerContainer}>
        <Image src={logo} style={styles.logo} />
        <Text style={styles.headerTitle1}>KATSINA STATE GOVERNMENT</Text>
        <Text style={styles.headerTitle2}>MINISTRY OF FINANCE</Text>
        <Text style={styles.headerTitle3}>CAPITAL PROJECT PAYMENT VOUCHER (MAIN)</Text>
      </View>

      {/* Main Body Container */}
      <View style={styles.bodyContainer}>
        {/* Left Column (75%) */}
        <View style={styles.leftColumn}>
          <Text style={styles.pvTitle}>PAYMENT VOUCHER</Text>
          <Text style={styles.formFieldBold}>KTT.44 (Capital Project)</Text>

          <Text style={styles.formField}>
            Organisation: {pv?.mda_name || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Organisation Name: {pv?.sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Organisation Name: {pv?.sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Sub Organisation Name: {pv?.sub_sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Payment to: {pv?.payment_to || "................................................................................................................."}
          </Text>

          {/* Main Table with Tax Summary */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}>Date</Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}>Detailed description of service</Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}>Rate</Text>
              <Text style={[styles.tableHeader, { width: '25%' }]}>Amount(NGN)</Text>
            </View>

            {/* Sub header for amount columns */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '20%' }]}>NGN</Text>
              <Text style={[styles.tableHeader, { width: '5%' }]}>K</Text>
            </View>

            {/* Main Description Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '15%' }]}></Text>
              <Text style={[styles.tableCell, {
                width: '50%',
                textAlign: 'left',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                paddingTop: 8,
                minHeight: 60
              }]}>
                {capitalizeFirstLetter(pv?.narration) || ""}
              </Text>
              <Text style={[styles.tableCell, { width: '10%' }]}></Text>
              <Text style={[styles.tableCell, { width: '20%', textAlign: 'right' }]}>
                {formatNumber(pv?.gross_amount || pv?.amount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%' }]}>
                {(pv?.gross_amount || pv?.amount)?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>

            {/* Tax Summary Rows */}
            {taxes.map((tax, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: '15%' }]}></Text>
                <Text style={[styles.tableCell, { width: '50%', paddingLeft: 20 }]}>
                  {tax.tax_name}
                </Text>
                <Text style={[styles.tableCell, { width: '10%' }]}></Text>
                <Text style={[styles.tableCell, { width: '20%', textAlign: 'right' }]}>
                  ({formatNumber(tax.amount || 0)?.split(".")[0]})
                </Text>
                <Text style={[styles.tableCell, { width: '5%' }]}>
                  {tax.amount?.toString()?.split(".")[1] || "00"}
                </Text>
              </View>
            ))}

            {/* Net Amount Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '65%', textAlign: 'justify', fontFamily: 'Helvetica-Bold' }]}>
                NET AMOUNT IN WORDS: {toWordsconver(netAmount.toString().split(".")[0])?.toUpperCase()} NAIRA
                {netAmount.toString().split(".")[1] && netAmount.toString().split(".")[1] !== "00"
                  ? ` AND ${toWordsconver(netAmount.toString().split(".")[1])?.toUpperCase()} KOBO`
                  : null} ONLY
              </Text>
              <Text style={[styles.tableCell, { width: '30%', textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>
                {formatNumber(netAmount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%', fontFamily: 'Helvetica-Bold' }]}>
                {netAmount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>
          </View>

          {/* Rest of the standard PV layout */}
          <StandardPVCertificationSections
            pendingSignature={pendingSignature}
            checkedSignature={checkedSignature}
            approvedSignature={approvedSignature}
            redivedSignature={redivedSignature}
            pv={pv}
          />
        </View>

        {/* Right Column (25%) */}
        <StandardPVRightColumn pv={pv} />
      </View>
    </Page>
  );
};

// Capital Project Tax PV Page Component
const CapitalProjectTaxPVPage = ({ pv, tax, capitalizeFirstLetter }) => {
  const pendingSignature = pv.history ? pv.history.find((a) => a.pv_status === "Pending") : null;
  const checkedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Checked") : null;
  const approvedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Approved") : null;
  const redivedSignature = pv.history ? pv.history.find((a) => a.pv_status === "Reviewed") : null;

  return (
    <Page size="A3" style={styles.page}>
      {/* Header Section */}
      <View style={styles.headerContainer}>
        <Image src={logo} style={styles.logo} />
        <Text style={styles.headerTitle1}>KATSINA STATE GOVERNMENT</Text>
        <Text style={styles.headerTitle2}>MINISTRY OF FINANCE</Text>
        <Text style={styles.headerTitle3}>PAYMENT VOUCHER</Text>
      </View>

      {/* Main Body Container */}
      <View style={styles.bodyContainer}>
        {/* Left Column (75%) */}
        <View style={styles.leftColumn}>
          {/* <Text style={styles.pvTitle}>TAX PAYMENT VOUCHER</Text> */}
          <Text style={styles.formFieldBold}>KTT.44 (Tax: {tax?.tax_name})</Text>

          <Text style={styles.formField}>
            Organisation: {pv?.mda_name || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Organisation Name: {pv?.sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Organisation Name: {pv?.sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Sub Sub Sub Organisation Name: {pv?.sub_sub_sub || "......................................................................................................"}
          </Text>
          <Text style={styles.formField}>
            Payment to: {tax?.tax_name} (Tax Authority)
          </Text>

          {/* Tax Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}>Date</Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}>Detailed description of service</Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}>Rate</Text>
              <Text style={[styles.tableHeader, { width: '25%' }]}>Amount(NGN)</Text>
            </View>

            {/* Sub header for amount columns */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableHeader, { width: '15%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '50%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '10%' }]}></Text>
              <Text style={[styles.tableHeader, { width: '20%' }]}>NGN</Text>
              <Text style={[styles.tableHeader, { width: '5%' }]}>K</Text>
            </View>

            {/* Tax Description Row */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCellLarge, { width: '15%' }]}></Text>
              <Text style={[styles.tableCellLarge, {
                width: '50%',
                textAlign: 'left',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                paddingTop: 8
              }]}>
                {tax?.tax_name} - {capitalizeFirstLetter(pv?.narration) || ""}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '10%', textAlign: 'center' }]}>
                {tax?.rate ? `${tax.rate}%` : ''}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '20%', textAlign: 'right' }]}>
                {formatNumber(tax?.amount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCellLarge, { width: '5%' }]}>
                {tax?.amount?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>

            {/* Table Footer */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: '65%', textAlign: 'justify' }]}>
                TOTAL AMOUNT IN WORDS: {toWordsconver((tax?.amount || 0).toString().split(".")[0])?.toUpperCase()} NAIRA
                {(tax?.amount || 0).toString().split(".")[1] && (tax?.amount || 0).toString().split(".")[1] !== "00"
                  ? ` AND ${toWordsconver((tax?.amount || 0).toString().split(".")[1])?.toUpperCase()} KOBO`
                  : null} ONLY
              </Text>
              <Text style={[styles.tableCell, { width: '30%', textAlign: 'right' }]}>
                {formatNumber(tax?.amount || 0)?.split(".")[0]}
              </Text>
              <Text style={[styles.tableCell, { width: '5%' }]}>
                {(tax?.amount || 0)?.toString()?.split(".")[1] || "00"}
              </Text>
            </View>
          </View>

          {/* Rest of the standard PV layout */}
          <StandardPVCertificationSections
            pendingSignature={pendingSignature}
            checkedSignature={checkedSignature}
            approvedSignature={approvedSignature}
            redivedSignature={redivedSignature}
            pv={pv}
          />
        </View>

        {/* Right Column (25%) */}
        <StandardPVRightColumn pv={pv} />
      </View>
    </Page>
  );
};

// Reusable Certification Sections Component
const StandardPVCertificationSections = ({
  pendingSignature,
  checkedSignature,
  approvedSignature,
  redivedSignature,
  pv
}) => (
  <>
    {/* First Certification Section */}
    <View style={styles.certificationSection}>
      <Text style={styles.formField}>
        We certify that the details above are in accordance with the relevant contract, regulations or other authority under which the Services/Goods were provided/purchased
      </Text>
      <View style={styles.signatureRow}>
        <Text style={styles.signatureText}>Officer who prepared voucher, Signature:</Text>
        {pendingSignature?.signature && (
          <Image src={pendingSignature.signature} style={styles.signatureImage} />
        )}
      </View>
      <Text style={styles.nameText}>
        Name (in block letters): {pendingSignature?.fullname || ""}
      </Text>
      <View style={styles.signatureRow}>
        <Text style={styles.signatureText}>Officer who checked voucher, Signature:</Text>
        {checkedSignature?.signature && (
          <Image src={checkedSignature.signature} style={styles.signatureImage} />
        )}
      </View>
      <Text style={styles.nameText}>
        Name (in block letters): {checkedSignature?.fullname || ""}
      </Text>
    </View>

    {/* Second Certification Section */}
    <View style={styles.certificationSection}>
      <Text style={styles.formField}>
        We certify that the Service/Goods have been duly performed/received, that financial authority GW/.............. is held to incur this expenditure and that the relevant D V.E account entries have been made. We hereby authorized payment.
      </Text>
    </View>

    {/* Accounting Officer Section */}
    <View style={{ marginBottom: 6, marginTop: 5 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={styles.formField}>Signature:</Text>
        {approvedSignature?.signature && (
          <Image src={approvedSignature.signature} style={styles.signatureImage} />
        )}
      </View>
      <Text style={styles.formField}>(Accounting Officer)</Text>
      <View style={{ flexDirection: 'row', width: '100%', marginTop: 2 }}>
        <Text style={[styles.formField, { width: '50%', textTransform: 'uppercase' }]}>
          Name (in block letters): {approvedSignature?.fullname || ""}
        </Text>
        <Text style={[styles.formField, { width: '50%' }]}>
          Date: {pv?.pv_date}
        </Text>
      </View>
    </View>

    {/* Second Accounting Officer Section */}
    <View style={{ marginBottom: 8, marginTop: 5 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={styles.formField}>Signature:</Text>
        {redivedSignature?.signature && (
          <Image src={redivedSignature.signature} style={styles.signatureImage} />
        )}
      </View>
      <Text style={styles.formField}>(Accounting Officer)</Text>
      <View style={{ flexDirection: 'row', width: '100%', marginTop: 2 }}>
        <Text style={[styles.formField, { width: '50%', textTransform: 'uppercase' }]}>
          Name (in block letters): {redivedSignature?.fullname || ""}
        </Text>
        <Text style={[styles.formField, { width: '50%' }]}>
          Date: {pv?.pv_date}
        </Text>
      </View>
    </View>

    {/* Payment Details Section */}
    <View style={[styles.certificationSection, { borderTop: '1px solid #000', marginTop: 6 }]}>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <Text style={[styles.formField, { width: '50%' }]}>Cheque No:</Text>
        <Text style={[styles.formField, { width: '50%' }]}>
          Account No: {pv?.contractor_acc_no}
        </Text>
      </View>
    </View>

    <View style={[styles.certificationSection, { borderTop: 0, marginTop: 2 }]}>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <Text style={[styles.formField, { width: '50%' }]}>
          Account Name: {pv?.contractor_acc_name}
        </Text>
        <Text style={[styles.formField, { width: '50%' }]}>
          Name of Bank: {pv?.contractor_bank_name}
        </Text>
      </View>
    </View>

    {/* Receipt Section */}
    <View style={styles.receiptSection}>
      <Text style={styles.receiptText}>
        Received the sum of .....................................Naira and......................................Kobo in
      </Text>
      <Text style={styles.receiptText}>
        acquisition of the above Account this.............................Day of..........................20..........
      </Text>
      <Text style={styles.receiptText}>
        Amount in word.....................................Name of Payee...................................................
      </Text>
      <Text style={styles.receiptText}>
        Witness in official Name..........................................Rank.................................................
      </Text>

      <View style={styles.witnessSection}>
        <View style={styles.witnessColumn}>
          <Text style={styles.receiptText}>Witness</Text>
          <Text style={styles.receiptText}>Official</Text>
        </View>
        <View style={styles.witnessColumnRight}>
          <Text style={styles.receiptText}>Name.......................................</Text>
          <Text style={styles.receiptText}>Rank........................................</Text>
        </View>
      </View>
    </View>
  </>
);

// Reusable Right Column Component
const StandardPVRightColumn = ({ pv }) => (
  <View style={styles.rightColumn}>
    {/* Voucher Number */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Voucher Number</Text>
      <View style={styles.codeContainer}>
        {String(pv?.pv_code || "").split("").map((digit, digitIndex) => (
          <Text
            key={digitIndex}
            style={[
              styles.codeDigit,
              {
                width: `${100 / Math.max(String(pv?.pv_code || "").length, 1)}%`,
                borderRight: digitIndex < String(pv?.pv_code || "").length - 1 ? '1px solid #000' : 0,
              }
            ]}
          >
            {digit}
          </Text>
        ))}
      </View>
    </View>

    {/* Date */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Year/Month/Day</Text>
      <View style={styles.codeContainer}>
        {String(pv?.date || "").split("").map((digit, digitIndex) => (
          <Text
            key={digitIndex}
            style={[
              styles.codeDigit,
              {
                width: `${100 / Math.max(String(pv?.date || "").length, 1)}%`,
                borderRight: digitIndex < String(pv?.date || "").length - 1 ? '1px solid #000' : 0,
              }
            ]}
          >
            {digit}
          </Text>
        ))}
      </View>
    </View>

    {/* Administrative Code */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Administrative Code</Text>
      <View style={styles.codeContainer}>
        {String(pv?.mda_code || "").split("").map((digit, digitIndex) => (
          <Text
            key={digitIndex}
            style={[
              styles.codeDigit,
              {
                width: `${100 / Math.max(String(pv?.mda_code || "").length, 1)}%`,
                borderRight: digitIndex < String(pv?.mda_code || "").length - 1 ? '1px solid #000' : 0,
              }
            ]}
          >
            {digit}
          </Text>
        ))}
      </View>
    </View>

    {/* Economic Code */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Economic Code</Text>
      <View style={styles.codeContainer}>
        {String(pv?.economic_code || "").split("").map((digit, digitIndex) => (
          <Text
            key={digitIndex}
            style={[
              styles.codeDigit,
              {
                width: `${100 / Math.max(String(pv?.economic_code || "").length, 1)}%`,
                borderRight: digitIndex < String(pv?.economic_code || "").length - 1 ? '1px solid #000' : 0,
              }
            ]}
          >
            {digit}
          </Text>
        ))}
      </View>
    </View>

    {/* Program Code */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Program Code</Text>
      <View style={styles.codeContainer}>
        {String(pv?.program_code || "").split("").map((digit, digitIndex) => (
          <Text
            key={digitIndex}
            style={[
              styles.codeDigit,
              {
                width: `${100 / Math.max(String(pv?.program_code || "").length, 1)}%`,
                borderRight: digitIndex < String(pv?.program_code || "").length - 1 ? '1px solid #000' : 0,
              }
            ]}
          >
            {digit}
          </Text>
        ))}
      </View>
    </View>

    {/* Functional Code */}
    <View style={styles.rightColumnSection}>
      <Text style={styles.rightColumnTitle}>Functional Code</Text>
      <View style={styles.codeContainer}>
        {/* Empty for functional code as in original */}
      </View>
    </View>

    {/* Treasury Section */}
    <View style={styles.treasurySection}>
      <Text style={styles.treasuryText}>Date received in Treasury</Text>
      <Text style={styles.treasuryText}>............................................</Text>
      <Text style={styles.treasuryText}>Check and passed at</Text>
      <Text style={styles.treasuryText}>............................................</Text>
      <Text style={styles.treasuryText}>For</Text>
      <Text style={styles.treasuryText}>Payable only at</Text>
      <Text style={styles.treasuryText}>Checked by internal Auditor</Text>
      <Text style={styles.treasuryText}>(Name and Signature)</Text>
    </View>
  </View>
);